<template>
  <div>
    <FormLabel
      :label="label"
      :required="required"
      :label-hint="labelHint"
    />
    <p
      v-if="description"
      class="text-sm text-slate-500 -mt-1 mb-2"
    >{{ description }}</p>
    <div class="relative">
      <div
        v-if="!props.inline"
        class="relative flex w-full cursor-pointer appearance-none items-center gap-2 rounded-lg border px-4 leading-tight text-slate-900 py-2.5 focus:outline-none"
        :class="inputClasses"
        @click="toggleShow"
      >
        <CalendarIcon
          class="-ml-1 h-4 w-4"
          :class="(modelValue === null || modelValue === '') && 'opacity-70'"
          aria-hidden="true"
        />
        <span
          v-if="modelValue === null || modelValue === ''"
          class="opacity-70"
        >{{ placeholder }}</span
        >
        <span v-else>{{ formattedLocalDatetime }}</span>
        <button
          type="button"
          aria-label="reset"
          class="absolute top-0 right-0 z-50 text-slate-400 hover:text-slate-900"
        >
          <XMarkIcon
            v-if="modelValue"
            class="h-10 w-10 p-3"
            aria-hidden="true"
            @click="updateModelValue(null)"
          />
        </button>
      </div>
      <DatePicker
        :attributes='attrs'
        :popover="popover"
        :masks="masks"
        :model-value="modelValueDate"
        :mode="mode"
        :timezone="timezone"
        @update:modelValue="updateModelValue"
      >
        <template v-slot="{ inputValue, inputEvents }">
          <input
            :value="inputValue"
            v-on="inputEvents"
            :name="name"
            class="absolute inset-0 h-full w-full cursor-pointer appearance-none border text-base leading-tight text-slate-900 opacity-0 py-2.5 focus:outline-none dark:bg-slate-600 dark:text-white"
            :placeholder="placeholder || 'Select Date'"
            :required="required"
          />
        </template>
      </DatePicker>
    </div>
    <FormDropdownSearch
      v-if="!hideTimezone"
      v-model="timezone"
      search-placeholder="Search timezones"
      class="mt-2"
      search-key="name"
      placeholder="Select a timezone"
      :options="timezones"
      show-value prefix="Timezone: "
      @update:modelValue="updateTimezone"
    />
  </div>
</template>

<script setup>
import FormLabel from "./FormLabel.vue";
import FormDropdownSearch from "./FormDropdownSearch.vue";
import {CalendarIcon, XMarkIcon} from "@heroicons/vue/24/outline";
import "v-calendar/dist/style.css";
import {DatePicker} from "v-calendar";
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import {timezones} from "../constants/Lists.js";

const emits = defineEmits(["update:modelValue", "update:timezone"]);
const props = defineProps({
  modelValue: {
    type: [Object, String, Date],
    default: null,
  },
  label: {
    type: String,
    default: "",
  },
  placeholder: {
    type: String,
    default: "Select Date",
  },
  required: Boolean,
  format: String,
  monthPicker: Boolean,
  labelHint: String,
  inline: Boolean,
  name: String,
  mode: {
    type: String,
    default: "date",
  },
  inputClasses: {
    type: String,
    default: "bg-slate-100",
  },
  popoverPlacement: {
    type: String,
    default: "top-start",
  },
  yearMonthDateOnly: Boolean,
  disableUtc: Boolean,
  hideTimezone: Boolean,
  emitsDateObject: Boolean,
  description: String,
  readonly: Boolean,
});

const show = ref(!!props.inline);
const timezone = ref(Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York");
if (!props.readonly) emits("update:timezone", timezone.value);

const toggleShow = () => {
  if (props.readonly) return;
  show.value = !show.value;
};

const attrs = ref([{
  key: "today",
  dot: {
    color: "green",
  },
  dates: new Date(),
}]);

const handleUTC = (date) => {
  if (!date) return null;

  if (props.yearMonthDateOnly) {
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split("-");
      return new Date(year, month - 1, day, 12, 0, 0, 0);
    }

    const currentDate = new Date(date);
    return new Date(currentDate.toISOString().split("T")[0]);
  }

  if (props.disableUtc) return new Date(date);
  const utcDate = new Date(date);
  return new Date(utcDate.toLocaleString("en-US", {timezone: timezone.value}));
};

const modelValueDate = ref(handleUTC(props.modelValue));

const formattedLocalDatetime = computed(() => {
  let options;

  if (props.mode === "dateTime") {
    options = {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timezone: timezone.value,
    };
  } else if (props.hideTimezone && props.disableUtc) {
    options = {year: "numeric", month: "short", day: "numeric"};
  } else {
    options = {year: "numeric", month: "short", day: "numeric", timezone: timezone.value};
  }

  return modelValueDate.value.toLocaleString("en-US", options);
});

const updateModelValue = (value) => {
  if (props.readonly) return;

  if (!value) return emits("update:modelValue", null);

  let date = new Date(value);

  if (props.mode === "startOfDay") {
    date.setHours(0, 0, 0, 0);
  } else if (props.mode === "endOfDay") {
    date.setHours(23, 59, 59, 999);
  } else if (props.mode !== "dateTime") {
    const currentDate = new Date();
    date.setHours(currentDate.getHours(), currentDate.getMinutes(), 0);
  }

  if (props.yearMonthDateOnly) {
    date.setHours(12, 0, 0, 0);
    emits("update:modelValue", date.toISOString().split("T")[0]);
  } else if (props.emitsDateObject) return emits("update:modelValue", date);
  else emits("update:modelValue", date.toISOString());
};

const updateTimezone = () => {
  if (props.readonly) return;
  updateModelValue(null);
  emits("update:timezone", timezone.value);
};

watch(() => props.modelValue, () => {
  modelValueDate.value = handleUTC(props.modelValue);
});

const popover = ref({
  placement: props.popoverPlacement,
});

const masks = {
  input: ["YYYY/MM/DD"],
  data: ["YYYY/MM/DD"],
  inputDateTime: ["YYYY/MM/DD h:mm A"],
  inputDateTime24hr: ["YYYY/MM/DD HH:mm"]
};

const handleWindowDateUpdate = (e) => {
  const date = e.detail?.value;
  if (!date) return;

  modelValueDate.value = handleUTC(date);
  updateModelValue(date);
};

const eventName = props.name ? `form-date-update-${props.name}` : null;

onMounted(() => {
  if (eventName) window.addEventListener(eventName, handleWindowDateUpdate);
});

onBeforeUnmount(() => {
  if (eventName) window.removeEventListener(eventName, handleWindowDateUpdate);
});
</script>

<style>
.vc-popover-content-wrapper {
  z-index: 10000;
}
</style>
